
import { defineComponent, inject } from 'vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { QuoteService } from '@/services/quote.service'
import { QuoteDetails } from '@/@types/responses/quote/QuoteDetails'
import { BuildTypeService } from '@/services/buildType.service'
import CustomerSearchMachine from '@/components/elements/CustomerSearchMachine.vue'
import NoteTile from '@/components/elements/NoteTile.vue'
import PlusSvg from '@/components/svgs/PlusSvg.vue'
import { Note } from '@/@types/responses/note/Note'
import NoteModal from '@/components/modals/NoteModal.vue'
import AddPartModal from '@/components/modals/AddPartModal.vue'
import AddServiceModal from '@/components/modals/AddServiceModal.vue'
import DotsSvg from '@/components/svgs/DotsSvg.vue'
import { Customer } from '@/@types/responses/customer/Customer'
import TextArea from '@/components/shared/TextArea.vue'
import { NoteService } from '@/services/note.service'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import CommentSvg from '@/components/svgs/CommentSvg.vue'
import { BuildTypeDropdownOption } from '@/@types/responses/build_type/BuildTypeDropdownOption'
import { QuotePartService } from '@/services/quotePart.service'
import { QuoteServiceService } from '@/services/quoteService.service'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { CreateQuoteMessage } from '@/@types/messages/CreateQuoteMessage'
import { UpdateQuoteMessage } from '@/@types/messages/UpdateQuoteMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import { QuoteServiceDetails } from '@/@types/responses/quote/QuoteServiceDetails'
import { QuotePartDetails } from '@/@types/responses/quote/QuotePartDetails'
import { UpdateQuotePartMessage } from '@/@types/messages/UpdateQuotePartMessage'
import { UpdateQuoteServiceMessage } from '@/@types/messages/UpdateQuoteServiceMessage'
import DragAndDropSvg from '@/components/svgs/DragAndDropSvg.vue'
import draggable from 'vuedraggable'
import { UpdateQuotePartsMessage } from '@/@types/messages/UpdateQuotePartsMessage'
import { UpdateQuoteServicesMessage } from '@/@types/messages/UpdateQuoteServicesMessage'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'Quote',
  components: {
    AddPartModal,
    AddServiceModal,
    AuditInformation,
    CartridgeSearchMachine,
    CommentSvg,
    CurrencyInput,
    CustomerSearchMachine,
    DotsSvg,
    DragAndDropSvg,
    draggable,
    Dropdown,
    LoadingPulse,
    LockingButton,
    NoteModal,
    NoteTile,
    NumericInput,
    PlusSvg,
    TextArea,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showAddPartModal || this.showAddServiceModal || this.showNoteModal || this.showQuotePartNoteModal || this.showQuoteServiceNoteModal) {
      this.showAddPartModal = false
      this.showAddServiceModal = false
      this.showNoteModal = false
      this.showQuotePartNoteModal = false
      this.showQuoteServiceNoteModal = false

      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const quotePartService = inject(PROVIDER_CONSTANTS.quotePartService) as QuotePartService
    const quoteService = inject(PROVIDER_CONSTANTS.quoteService) as QuoteService
    const quoteServiceService = inject(PROVIDER_CONSTANTS.quoteServiceService) as QuoteServiceService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      buildTypeService,
      noteService,
      quotePartService,
      quoteService,
      quoteServiceService,
      stateService,
      toast
    }
  },
  data() {
    let noteToEdit: Note|undefined
    let quote: QuoteDetails|undefined
    let quotePartNoteToEdit: Note|undefined
    let quoteServiceNoteToEdit: Note|undefined
    let unchangedQuote: QuoteDetails|undefined

    return {
      addingNote: false,
      buildTypeDropdownOptions: [] as BuildTypeDropdownOption[],
      convertingToOrder: false,
      creatingQuote: false,
      loading: true,
      newNoteText: '',
      noteToEdit,
      quote,
      quotePartId: 0,
      quotePartNoteToEdit,
      quoteServiceId: 0,
      quoteServiceNoteToEdit,
      receivedCustomerApproval: false,
      saving: false,
      sendingQuoteToCustomer: false,
      showAddPartModal: false,
      showAddServiceModal: false,
      showMenu: false,
      showNoteModal: false,
      showQuotePartNoteModal: false,
      showQuoteServiceNoteModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      unchangedQuote
    }
  },
  computed: {
    addNoteEnabled(): boolean {
      return this.newNoteText.trim() !== ''
    },
    cartridgeRequired(): boolean {
      const index = this.buildTypeDropdownOptions.findIndex(b => b.id === this.quote?.buildTypeId)
      if (index === -1)
        return false

      return this.buildTypeDropdownOptions[index].cartridgeRequired
    },
    isDirty(): boolean {
      const a = this.quote
      const b = this.unchangedQuote

      let validCartridgeValue = true
      if (this.cartridgeRequired && !a?.cartridgeId)
        validCartridgeValue = false

      return (a?.cartridgeId !== b?.cartridgeId ||
             a?.customerId !== b?.customerId ||
             a?.buildTypeId !== b?.buildTypeId ||
             a?.partsDiscountPercentage !== b?.partsDiscountPercentage ||
             a?.servicesDiscountPercentage !== b?.servicesDiscountPercentage ||
             a?.customerSuppliedParts !== b?.customerSuppliedParts) && (a?.buildTypeId !== undefined && a?.customerId !== undefined && validCartridgeValue && a?.partsDiscountPercentage !== undefined && a?.partsDiscountPercentage >= 0 && a?.servicesDiscountPercentage !== undefined && a?.servicesDiscountPercentage >= 0)
    },
    stateDisplayValue(): string|undefined {
      if (this.quote && this.quote.customer?.stateId) {
        const stateDropdownOption = this.stateDropdownOptions.find(o => o.id == this.quote?.customer?.stateId)
        return stateDropdownOption?.name
      }

      return undefined
    },
    titleText(): string {
      return !this.quote || this.quote.id === 0 ? 'Create Quote' : `Quote #${this.id}`
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    Promise.all([this.getBuildTypes(), this.getStates()]).then(() => {
      if (this.id > 0) {
        this.getQuote()
      } else {
        this.quote = new QuoteDetails(0)
        this.unchangedQuote = new QuoteDetails(0)
        this.loading = false
      }
    })
  },
  methods: {
    closeMenu(): void {
      this.showMenu = false
    },
    getBuildTypes(): Promise<void> {
      return this.buildTypeService.getBuildTypeDropdownOptions().then(dropdownOptionsResponse => {
        this.buildTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build types. Try reloading the page.')
        }
      })
    },
    getQuote(): Promise<void> {
      return this.quoteService.getQuote(this.quote ? this.quote.id : this.id).then(quote => {
        this.quote = quote
        this.unchangedQuote = JSON.parse(JSON.stringify(quote))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false
        this.sendingQuoteToCustomer = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the quote. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onAddNoteClicked(): void {
      this.addingNote = true
      const noteMessage = new NoteMessage(this.newNoteText, false)
      this.quoteService.createQuoteNote(this.id, noteMessage).then(() => {
        this.getQuote().then(() => {
          this.newNoteText = ''
          this.addingNote = false
          this.toast.success('Note added successfully.')
        })
      }).catch(error => {
        this.addingNote = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to save the note. Please try again.')
        }
      })
    },
    onAddPartClicked(): void {
      this.showAddPartModal = true
    },
    onAddQuotePartNoteClicked(quotePartId: number): void {
      this.quotePartId = quotePartId
      this.quotePartNoteToEdit = new Note(0)
      this.showQuotePartNoteModal = true
    },
    onAddServiceClicked(): void {
      this.showAddServiceModal = true
    },
    onAddServiceNoteClicked(quoteServiceId: number): void {
      this.quoteServiceId = quoteServiceId
      this.quoteServiceNoteToEdit = new Note(0)
      this.showQuoteServiceNoteModal = true
    },
    onConvertToOrderClicked(): void {
      this.convertingToOrder = true
      this.quoteService.createOrder(this.id).then(objectCreatedResponse => {
        this.convertingToOrder = false
        this.$router.replace(`/order/${objectCreatedResponse.id}`)
      }).catch(error => {
        this.convertingToOrder = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to covert this quote to an order. Please try again.')
        }
      })
    },
    onCustomerSelected(customer: Customer): void {
      if (!this.quote) return

      this.quote.customer = customer
      this.quote.customerId = customer.id
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The note was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onDuplicateClicked(): void {
      this.creatingQuote = true

      this.quoteService.createQuoteFromQuote(this.id).then(objectCreatedResponse => {
        this.toast.success('A new quote was created from the existing quote successfully.')
        window.location.href = '/quote/' + objectCreatedResponse.id
      }).catch(error => {
        this.creatingQuote = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to duplicate the quote as a new quote. Please try again.')
        }
      })
    },
    onEditCustomerClicked(): void {
      if (!this.quote || !this.quote.customerId) return

      const routeData = this.$router.resolve('/customer/' + this.quote.customerId)
      window.open(routeData.href, '_blank')
    },
    onEditNote(note: Note): void {
      this.noteToEdit = note
      this.showNoteModal = true
    },
    onEditQuotePartNote(note: Note): void {
      this.quotePartNoteToEdit = note
      this.showQuotePartNoteModal = true
    },
    onEditQuoteServiceNote(note: Note): void {
      this.quoteServiceNoteToEdit = note
      this.showQuoteServiceNoteModal = true
    },
    onQuotePartChanged(quotePart: QuotePartDetails): void {
      const updateQuotePartMessage = new UpdateQuotePartMessage(quotePart.price)
      this.quotePartService.updateQuotePart(quotePart.id, updateQuotePartMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The part was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the part. Please try again.')
        }
      })
    },
    onQuotePartsDisplayOrderChanged(): void {
      if (!this.quote) return

      const updateQuotePartsMessage = new UpdateQuotePartsMessage(this.quote.quoteParts.map(q => q.id))
      this.quotePartService.updateQuoteParts(updateQuotePartsMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The part was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the part. Please try again.')
        }
      })
    },
    onQuoteServiceChanged(quoteService: QuoteServiceDetails): void {
      const updateQuoteServiceMessage = new UpdateQuoteServiceMessage(quoteService.price)
      this.quoteServiceService.updateQuoteService(quoteService.id, updateQuoteServiceMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The service was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the service. Please try again.')
        }
      })
    },
    onQuoteServicesDisplayOrderChanged(): void {
      if (!this.quote) return

      const updateQuoteServicesMessage = new UpdateQuoteServicesMessage(this.quote.quoteServices.map(q => q.id))
      this.quoteServiceService.updateQuoteServices(updateQuoteServicesMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The service was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the service. Please try again.')
        }
      })
    },
    onReceivedCustomerApprovalClicked(): void {
      if (!this.quote || this.receivedCustomerApproval || !this.quote.customerId || !this.quote.buildTypeId) return

      this.receivedCustomerApproval = true

      const updateQuoteMessage = new UpdateQuoteMessage(this.quote.buildTypeId, this.quote.cartridgeId, this.quote.customerSuppliedParts, this.quote.partsDiscountPercentage, true, false, this.quote.servicesDiscountPercentage)
      this.quoteService.updateQuote(this.id, updateQuoteMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The quote was approved successfully.')
          this.receivedCustomerApproval = false
        })
      }).catch(error => {
        this.receivedCustomerApproval = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to approve the quote. Please try again.')
        }
      })
    },
    onRemoveCustomerClicked(): void {
      if (!this.quote) return

      this.quote.customer = undefined
      this.quote.customerId = undefined
    },
    onRemoveQuotePartClicked(id: number): void {
      this.quotePartService.deleteQuotePart(id).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The part was removed from the quote successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the part from the quote. Please try again.')
        }
      })
    },
    onRemoveQuoteServiceClicked(id: number): void {
      this.quoteServiceService.deleteQuoteService(id).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The service was removed from the quote successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the service from the quote. Please try again.')
        }
      })
    },
    onSaveQuoteClicked(): void {
      if (!this.quote || !this.quote.customerId || !this.quote.buildTypeId) return

      this.saving = true

      if (this.quote.id === 0) {
        const createQuoteMessage = new CreateQuoteMessage(this.quote.buildTypeId, this.quote.cartridgeId, this.quote.customerId, this.quote.customerSuppliedParts, this.quote.partsDiscountPercentage, this.quote.servicesDiscountPercentage)
        this.quoteService.createQuote(createQuoteMessage).then(objectCreatedResponse => {
          if (!this.quote) return
          
          this.quote.id = objectCreatedResponse.id
          this.getQuote().then(() => {
            this.$router.replace(`/quote/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The quote was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the quote. Please try again.')
          }
        })
      } else {
        const updateQuoteMessage = new UpdateQuoteMessage(this.quote.buildTypeId, this.quote.cartridgeId, this.quote.customerSuppliedParts, this.quote.partsDiscountPercentage, false, false, this.quote.servicesDiscountPercentage)
        this.quoteService.updateQuote(this.id, updateQuoteMessage).then(() => {
          this.getQuote().then(() => {
            this.toast.success('The quote was updated successfully.')
            this.saving = false
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the quote. Please try again.')
          }
        })
      }
    },
    onSendQuoteToCustomerForApprovalClicked(): void {
      if (!this.quote || this.sendingQuoteToCustomer || !this.quote.customerId || !this.quote.buildTypeId) return

      this.sendingQuoteToCustomer = true

      const updateQuoteMessage = new UpdateQuoteMessage(this.quote.buildTypeId, this.quote.cartridgeId, this.quote.customerSuppliedParts, this.quote.partsDiscountPercentage, false, true, this.quote.servicesDiscountPercentage)
      this.quoteService.updateQuote(this.id, updateQuoteMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The quote was sent to the customer successfully.')
          this.sendingQuoteToCustomer = false
        })
      }).catch(error => {
        this.sendingQuoteToCustomer = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to send the quote to the customer. Please try again.')
        }
      })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.quote)
        document.title = `Quote #${this.quote.id} - Short Action Customs, LLC`
    }
  }
})
